/**
 * ----------------------------------------------------------------------------------------------------------------------
 * WAQAS CUSTOM PRODUCT CARD
 * ----------------------------------------------------------------------------------------------------------------------
 *
 * Loaded from layout/theme.liquid ONLY when settings.product_card_layout == 'waqas'.
 *
 * SCOPE — deltas only.
 * Impact's own <product-card> element (assets/theme.js) already handles, and is deliberately reused for:
 *   - swatch -> variant image cross-fade, with preload so there is no blank frame
 *   - rewriting product links to ?variant=<id> on swatch change
 *   - re-binding after Section Rendering API re-renders, via connectedCallback
 *
 * This file adds only what Impact does not do:
 *   1. Gating the swatch hover preview behind a merchant setting (Impact's is always-on)
 *   2. Swapping the price / compare-at / savings when the selected variant differs
 *   3. Add-to-cart button label states
 *
 * NO MONEY FORMATTING HAPPENS HERE, BY DESIGN.
 * `shop.money_format` may contain HTML and per-currency separators, so reimplementing Shopify's
 * formatter in JS produced visibly broken prices. Every string in `data-variant-prices` is already
 * formatted by Liquid; this file only swaps text.
 *
 * AJAX SAFETY (global rule 6): every listener is delegated on `document`, so cards injected by
 * collection filtering, sorting, pagination or quick-add work with no re-initialisation. There is
 * deliberately no querySelectorAll-on-load anywhere in this file, and nothing to re-run on
 * shopify:section:load.
 */

(function () {
  'use strict';

  var ADDED_LABEL_DURATION = 1600;

  /** Money strings may legitimately contain markup from the shop's money format. */
  function setMoney(node, value) {
    if (value.indexOf('<') === -1) {
      node.textContent = value;
    } else {
      node.innerHTML = value;
    }
  }

  /**
   * Rewrites the price node, applying the same optional "raised minor units" treatment as the Liquid
   * template. Splitting is skipped when the money string contains markup, or when it does not end in
   * <separator><2 digits> — currencies with no decimals are left whole.
   */
  function renderPrice(priceNode, moneyString, allowSplit) {
    var wholeNode = priceNode.querySelector('.waqas-card__price-whole');
    if (!wholeNode) return;

    var fracNode = priceNode.querySelector('.waqas-card__price-frac');
    var isPlain = moneyString.indexOf('<') === -1;
    var whole = moneyString;
    var frac = '';

    if (allowSplit && isPlain) {
      var separator = moneyString.slice(-3, -2);
      if (separator === '.' || separator === ',') {
        whole = moneyString.slice(0, -3);
        frac = moneyString.slice(-2);
      }
    }

    setMoney(wholeNode, whole);

    if (frac) {
      if (!fracNode) {
        fracNode = document.createElement('span');
        fracNode.className = 'waqas-card__price-frac';
        wholeNode.insertAdjacentElement('afterend', fracNode);
      }
      fracNode.textContent = frac;
    } else if (fracNode) {
      fracNode.remove();
    }
  }

  /**
   * Rebuilds the compare-at row. It is rebuilt rather than patched because a product can move between
   * "discounted" and "not discounted" as variants change, and the <s> element may not exist yet.
   */
  function renderCompare(compareNode, compareMoney, savingsText) {
    if (!compareMoney) {
      compareNode.innerHTML = '';
      return;
    }

    compareNode.innerHTML = '';

    var struck = document.createElement('s');
    struck.className = 'waqas-card__compare-price';
    setMoney(struck, compareMoney);
    compareNode.appendChild(struck);

    if (savingsText) {
      var savings = document.createElement('span');
      savings.className = 'waqas-card__savings';
      savings.setAttribute('data-waqas-savings', '');
      setMoney(savings, savingsText);
      compareNode.appendChild(savings);
    }
  }

  /* --------------------------------------------------------------------------------------------------------------------
     1. HOVER PREVIEW GATE
     --------------------------------------------------------------------------------------------------------------------

     Impact binds its hover preview with a CAPTURING listener on the <product-card> element itself.
     Because capture runs ancestors-first, a capturing listener on `document` fires earlier and can
     stop the event before Impact ever sees it. That lets the setting turn hover preview off without
     editing assets/theme.js, which is a compiled bundle.
  */
  document.addEventListener(
    'pointerover',
    function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;

      var label = target.closest('.waqas-card__swatch-list [type="radio"] + label, .waqas-card__swatch-list .color-swatch');
      if (!label) return;

      var card = label.closest('product-card');
      if (card && card.getAttribute('data-hover-preview') === 'false') {
        event.stopPropagation();
      }
    },
    true
  );

  /* --------------------------------------------------------------------------------------------------------------------
     2. PRICE UPDATE ON SWATCH CHANGE
     -------------------------------------------------------------------------------------------------------------------- */
  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!input || !input.matches || !input.matches('.waqas-card__swatch-list [type="radio"]')) return;

    var card = input.closest('product-card');
    if (!card) return;

    var variantId = input.getAttribute('data-variant-id');
    var rawMap = card.getAttribute('data-variant-prices');
    if (!variantId || !rawMap) return;

    var map;
    try {
      map = JSON.parse(rawMap);
    } catch (error) {
      return;
    }

    var entry = map[variantId];
    if (!entry || typeof entry.p !== 'string') return;

    var allowSplit = card.getAttribute('data-price-split') === 'true';

    var priceNode = card.querySelector('[data-waqas-price]');
    if (priceNode) renderPrice(priceNode, entry.p, allowSplit);

    var compareNode = card.querySelector('[data-waqas-compare]');
    if (compareNode) renderCompare(compareNode, entry.c, entry.s);
  });

  /* --------------------------------------------------------------------------------------------------------------------
     3. ADD-TO-CART BUTTON STATES

     Impact's `product-form` element owns the actual cart request and the cart-notification-drawer
     feedback. This only reflects that lifecycle in the button label.
     -------------------------------------------------------------------------------------------------------------------- */
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.querySelector) return;

    var button = form.querySelector('.waqas-card__button[data-label-adding]');
    if (!button) return;

    button.setAttribute('aria-busy', 'true');
    button.textContent = button.getAttribute('data-label-adding');
  });

  // Impact dispatches `variant:add` once the item is actually in the cart.
  document.addEventListener('variant:add', function (event) {
    var card = event.target && event.target.closest ? event.target.closest('product-card') : null;
    if (!card) return;

    var button = card.querySelector('.waqas-card__button[data-label-added]');
    if (!button) return;

    button.setAttribute('aria-busy', 'false');
    button.classList.add('is-added');
    button.textContent = button.getAttribute('data-label-added');

    window.setTimeout(function () {
      button.classList.remove('is-added');
      button.textContent = button.getAttribute('data-label-default');
    }, ADDED_LABEL_DURATION);
  });
})();
